import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Trash2, Euro, Plus, Building2 } from 'lucide-react';
import { getLogo, setLogo, getPrices, setPrice, parsePrice } from '../lib/license';
import { compressImage } from '../lib/model';
import { getCompany, setCompany, COMPANY_FIELDS } from '../lib/company';
import { useFeatures } from '../lib/features';
import PitPrices from './PitPrices';
import VatSetting from './VatSetting';
import MaintIntervals from './MaintIntervals';

function LogoBox({ t }) {
  const [logo, setLogoState] = useState(getLogo());
  const fileRef = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const img = await compressImage(f, 500, 0.92, '#ffffff');
    setLogo(img.data);
    setLogoState(img.data);
  };
  return (
    <section className="set-sec" data-testid="logo-section">
      <h5>{t.logoTitle}</h5>
      <div className="row">
        <div className="logo-prev" data-testid="logo-preview">{logo ? <img src={logo} alt="logo" /> : '—'}</div>
        <button className="btn btn-primary btn-sm" data-testid="logo-upload-btn" onClick={() => fileRef.current.click()}><Upload size={15} /> {t.logoUpload}</button>
        {logo && <button className="btn btn-outline btn-sm" data-testid="logo-remove-btn" onClick={() => { setLogo(null); setLogoState(null); }}><Trash2 size={15} /> {t.logoRemove}</button>}
        <input ref={fileRef} type="file" accept="image/*" hidden data-testid="logo-input" onChange={upload} />
      </div>
    </section>
  );
}

function CompanyBox({ t }) {
  const [c, setC] = useState(getCompany);
  const ch = (k, v) => { const n = { ...c, [k]: v }; setC(n); setCompany(n); };
  return (
    <section className="set-sec" data-testid="company-section">
      <h5>{t.coTitle}</h5>
      <div className="co-grid">
        {COMPANY_FIELDS.map((k) => (
          <label key={k} className={k === 'name' || k === 'addr' ? 'wide' : ''}>{t['co_' + k]}
            <input data-testid={`company-${k}-input`} type={k === 'email' ? 'email' : k === 'phone' ? 'tel' : 'text'} value={c[k] || ''} onChange={(e) => ch(k, e.target.value)} />
          </label>
        ))}
      </div>
      <p className="muted small-hint">{t.coHint}</p>
    </section>
  );
}

function PriceList({ t }) {
  const [prices, setPrices] = useState(getPrices());
  const [name, setName] = useState('');
  const [val, setVal] = useState('');
  const add = () => { if (name.trim() && parsePrice(val) != null) { setPrices(setPrice(name.trim(), val)); setName(''); setVal(''); } else toast.error(t.priceUnit + '?'); };
  return (
    <section className="set-sec" data-testid="price-list">
      <h5><Euro size={14} /> {t.priceTitle}</h5>
      {!Object.keys(prices).length && <p className="muted small-hint">{t.priceEmpty}</p>}
      {Object.entries(prices).map(([k, v], i) => (
        <div className="row price-row" key={k} data-testid={`price-row-${i}`}>
          <span className="price-name">{k}</span>
          <input type="text" inputMode="decimal" data-testid={`price-input-${i}`} defaultValue={String(v).replace('.', ',')} onBlur={(e) => setPrices(setPrice(k, e.target.value))} />
          <button className="icon-btn danger" data-testid={`price-delete-${i}`} onClick={() => setPrices(setPrice(k, null))}><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="row">
        <input type="text" data-testid="price-name-input" placeholder={t.priceName} value={name} onChange={(e) => setName(e.target.value)} />
        <input type="text" inputMode="decimal" data-testid="price-value-input" placeholder={t.priceUnit} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <button className="btn btn-dark btn-sm" data-testid="price-add-btn" onClick={add}><Plus size={14} /> {t.priceAdd}</button>
      </div>
    </section>
  );
}

// company master data: logo for the PDF header, prices, VAT, inspection intervals (moved out of the settings)
export default function MasterData({ t }) {
  const ft = useFeatures();
  return (
    <details className="md-block" data-testid="master-data" open>
      <summary data-testid="master-data-toggle"><Building2 size={16} /> {t.mdTitle}</summary>
      <LogoBox t={t} />
      <CompanyBox t={t} />
      {(ft.costs || ft.lv) && <PriceList t={t} />}
      {ft.pits && <PitPrices t={t} />}
      {(ft.costs || ft.lv) && <VatSetting t={t} />}
      {ft.maint && <MaintIntervals t={t} />}
    </details>
  );
}
